import React, { useState } from 'react';
import {
  Archive,
  Calendar,
  AlertTriangle,
  X
} from 'lucide-react';
import { BENEFICIARY_STATUS, ARCHIVE_REASONS } from '../../constants/beneficiaryStatuses';

/**
 * Modal d'archivage avancé pour les bénéficiaires
 */
const ArchiveBeneficiaryModal = ({ 
  client, 
  db, 
  onClose, 
  onSuccess 
}) => {
  const [archiveDate, setArchiveDate] = useState('');
  const [archiveReason, setArchiveReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [deleteAppointments, setDeleteAppointments] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Date d'aujourd'hui au format YYYY-MM-DD pour le champ date
  const today = new Date().toISOString().split('T')[0];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!archiveDate) {
      setError('Veuillez sélectionner une date d\'archivage');
      return;
    }

    if (!archiveReason) {
      setError('Veuillez sélectionner une raison d\'archivage');
      return;
    }

    if (archiveReason === 'autre' && !customReason.trim()) {
      setError('Veuillez préciser la raison de l\'archivage');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Confirmation finale si suppression des rendez-vous
      if (deleteAppointments) {
        const confirmMessage = `Êtes-vous sûr de vouloir archiver ${client.firstName} ${client.lastName} et supprimer tous ses rendez-vous à partir du ${new Date(archiveDate).toLocaleDateString('fr-FR')} ? Cette action est irréversible.`;
        
        if (!window.confirm(confirmMessage)) {
          setIsLoading(false);
          return;
        }
      } else {
        const confirmMessage = `Êtes-vous sûr de vouloir archiver ${client.firstName} ${client.lastName} ?`;
        
        if (!window.confirm(confirmMessage)) {
          setIsLoading(false);
          return;
        }
      }

      // Import des fonctions Firestore nécessaires
      const {
        updateDoc,
        doc,
        collection,
        query,
        where,
        getDocs,
        writeBatch,
        Timestamp,
        arrayUnion
      } = await import('firebase/firestore');

      // Déterminer la raison finale (personnalisée si "autre")
      const finalReason = archiveReason === 'autre' ? customReason.trim() : archiveReason;

      // 1. Mettre à jour le bénéficiaire avec les informations d'archivage
      await updateDoc(doc(db, 'clients', client.id), {
        // Nouveau système de statuts
        status: BENEFICIARY_STATUS.ARCHIVED,
        statusHistory: arrayUnion({
          oldStatus: client.status || BENEFICIARY_STATUS.ACTIVE,
          newStatus: BENEFICIARY_STATUS.ARCHIVED,
          reason: finalReason,
          customReason: archiveReason === 'autre' ? customReason.trim() : '',
          notes: '',
          changedBy: 'admin', // Utilisateur qui a effectué l'archivage
          changedAt: Timestamp.now()
        }),
        lastStatusChange: Timestamp.now(),

        // Ancien système (conservé pour compatibilité)
        isArchived: true,
        archiveDate: Timestamp.fromDate(new Date(archiveDate)),
        archiveReason: finalReason,
        archivedAt: Timestamp.now()
      });

      // 2. Supprimer les rendez-vous si demandé
      if (deleteAppointments) {
        const selectedDate = new Date(archiveDate);
        selectedDate.setHours(0, 0, 0, 0); // Début de la journée
        
        const appointmentsRef = collection(db, 'appointments');
        const q = query(
          appointmentsRef, 
          where('clientId', '==', client.id),
          where('date', '>=', Timestamp.fromDate(selectedDate))
        );
        
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const batch = writeBatch(db);
          
          // Analyser les rendez-vous pour gérer les séries récurrentes
          const appointmentsToDelete = [];
          const recurringSeries = new Map(); // Pour regrouper les séries récurrentes
          
          querySnapshot.forEach((document) => {
            const appointmentData = document.data();
            
            // Si c'est un rendez-vous récurrent avec une série
            if (appointmentData.isRecurring && appointmentData.recurrenceSeriesId) {
              const seriesId = appointmentData.recurrenceSeriesId;
              
              if (!recurringSeries.has(seriesId)) {
                recurringSeries.set(seriesId, []);
              }
              recurringSeries.get(seriesId).push({
                id: document.id,
                date: appointmentData.date,
                ...appointmentData
              });
            } else {
              // Rendez-vous individuel ou sans série - supprimer directement
              appointmentsToDelete.push(document.id);
              batch.delete(document.ref);
            }
          });
          
          // Pour chaque série récurrente, supprimer seulement les rendez-vous individuels
          // sans affecter la série complète
          for (const [seriesId, appointmentsInSeries] of recurringSeries) {
            appointmentsInSeries.forEach(appointment => {
              appointmentsToDelete.push(appointment.id);
              batch.delete(doc(db, 'appointments', appointment.id));
            });
            
            console.log(`Série récurrente ${seriesId}: ${appointmentsInSeries.length} rendez-vous supprimés`);
          }
          
          await batch.commit();
          console.log(`${appointmentsToDelete.length} rendez-vous supprimés pour ${client.firstName} ${client.lastName}`);
          
          // Optionnel: Afficher un message d'information sur les séries récurrentes
          if (recurringSeries.size > 0) {
            console.log(`${recurringSeries.size} séries récurrentes ont été partiellement supprimées (seulement les rendez-vous à partir de ${archiveDate})`);
          }
        }
      }

      // Succès
      if (onSuccess) {
        onSuccess({
          clientName: `${client.firstName} ${client.lastName}`,
          archiveDate,
          archiveReason: finalReason,
          appointmentsDeleted: deleteAppointments
        });
      }

      onClose();

    } catch (error) {
      console.error('Erreur lors de l\'archivage:', error);
      setError('Une erreur est survenue lors de l\'archivage. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 dark:bg-opacity-70">
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* En-tête */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <Archive className="text-orange-500 dark:text-orange-400 mr-3" size={24} />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Archiver le bénéficiaire
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Contenu */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Informations du bénéficiaire */}
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-blue-800 dark:text-blue-200 font-medium">
              {client.firstName} {client.lastName}
            </p>
            {client.email && (
              <p className="text-blue-600 dark:text-blue-400 text-sm mt-1">{client.email}</p>
            )}
          </div>

          {/* Date d'archivage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Calendar className="inline mr-2" size={16} />
              Date d'effet de l'archivage
            </label>
            <input
              type="date"
              value={archiveDate}
              onChange={(e) => setArchiveDate(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Les rendez-vous à partir de cette date seront supprimés si l'option est cochée (date passée ou future)
            </p>
          </div>

          {/* Raison de l'archivage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Raison de l'archivage *
            </label>
            <select
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="">Sélectionner une raison...</option>
              {ARCHIVE_REASONS.map(reason => (
                <option key={reason.value} value={reason.value}>
                  {reason.label}
                </option>
              ))}
            </select>
          </div>

          {/* Champ personnalisé si "Autre" est sélectionné */}
          {archiveReason === 'autre' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Précisez la raison *
              </label>
              <textarea
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="Décrivez la raison de l'archivage..."
                rows={3}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
          )}

          {/* Option de suppression des rendez-vous */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="deleteAppointments"
              checked={deleteAppointments}
              onChange={(e) => setDeleteAppointments(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
            />
            <label htmlFor="deleteAppointments" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
              Supprimer tous les rendez-vous à partir de la date choisie
            </label>
          </div>

          {/* Avertissement */}
          {deleteAppointments && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
              <div className="flex items-start">
                <AlertTriangle className="text-yellow-600 dark:text-yellow-400 mr-2 mt-0.5" size={16} />
                <div>
                  <p className="text-yellow-800 dark:text-yellow-200 text-sm font-medium">
                    Attention
                  </p>
                  <p className="text-yellow-700 dark:text-yellow-300 text-xs mt-1">
                    Tous les rendez-vous de ce bénéficiaire à partir du {archiveDate ? new Date(archiveDate).toLocaleDateString('fr-FR') : '...'} seront définitivement supprimés.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Message d'erreur */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              disabled={isLoading}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading || !archiveDate || !archiveReason}
              className="px-4 py-2 bg-orange-600 dark:bg-orange-500 text-white rounded-lg hover:bg-orange-700 dark:hover:bg-orange-600 disabled:bg-orange-300 dark:disabled:bg-orange-800 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <span className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Archivage...
                </span>
              ) : (
                'Archiver le bénéficiaire'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ArchiveBeneficiaryModal;
